import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

function ResetPassword() {
    const { t } = useLanguage();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [tokenError, setTokenError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [tokenChecked, setTokenChecked] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    
    const { token } = useParams();
    const navigate = useNavigate();
    
    // Verify token on component mount
    useEffect(() => {
        const verifyToken = async () => {
            try {
                const response = await api.get(`/auth/verify-reset-token/${token}`);
                setTokenValid(true);
            } catch (error) {
                setTokenError(error.response?.data?.message || t('auth.resetPassword.tokenInvalid'));
                setTokenValid(false);
            } finally {
                setTokenChecked(true);
            }
        };

        if (token) {
            verifyToken();
        } else {
            setTokenError(t('auth.resetPassword.invalidLink'));
            setTokenChecked(true);
        }
    }, [token, t]);
    
    const validatePassword = (value) => {
        if (!value) {
            setPasswordError(t('auth.resetPassword.passwordRequired'));
            return false;
        }

        if (value.length < 6) {
            setPasswordError(t('auth.resetPassword.passwordMinLength'));
            return false;
        }

        setPasswordError('');
        return true;
    };

    const validateConfirmPassword = (value) => {
        if (value !== password) {
            setConfirmError(t('auth.resetPassword.passwordsDoNotMatch'));
            return false;
        }

        setConfirmError('');
        return true;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const isPasswordValid = validatePassword(password);
        const isConfirmValid = validateConfirmPassword(confirmPassword);
        
        if (!isPasswordValid || !isConfirmValid) {
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await api.post('/auth/reset-password', {
                token,
                password
            });
            
            setSuccessMessage(response.data.message || t('auth.resetPassword.resetSuccess'));

            // Redirect to login after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error) {
            setTokenError(error.response?.data?.message || t('auth.resetPassword.resetFailed'));
        } finally {
            setLoading(false);
        }
    };
    
    if (!tokenChecked) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                </div>
                <p className="mt-3">{t('auth.resetPassword.verifyingLink')}</p>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="text-center mb-4">
                <img
                    src="/MUSI.ico"
                    alt="MUSI Logo"
                    className="img-fluid"
                    style={{ maxHeight: '120px' }}
                />
                <h2 className="mt-3">{t('auth.resetPassword.title')}</h2>
            </div>

            <div className="row justify-content-center">
                <div className="col-md-6">
                    {tokenError && (
                        <div className="alert alert-danger">
                            <p>{tokenError}</p>
                            <Link to="/forgot-password" className="btn btn-primary mt-2">
                                {t('auth.resetPassword.requestNewLink')}
                            </Link>
                        </div>
                    )}

                    {successMessage && (
                        <div className="alert alert-success">
                            <p>{successMessage}</p>
                            <div className="mt-2">
                                <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                                    <span className="visually-hidden">{t('auth.resetPassword.redirectingToLogin')}</span>
                                </div>
                                <span>{t('auth.resetPassword.redirectingToLogin')}</span>
                            </div>
                        </div>
                    )}

                    {tokenValid && !successMessage && (
                        <form onSubmit={handleSubmit} className="p-4 border rounded shadow-sm bg-white">
                            <p className="mb-3">
                                {t('auth.resetPassword.enterNewPassword')}
                            </p>

                            <div className="mb-3">
                                <label htmlFor="password" className="form-label">{t('auth.resetPassword.newPassword')}</label>
                                <input
                                    type="password"
                                    id="password"
                                    className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        validatePassword(e.target.value);
                                    }}
                                    required
                                />
                                {passwordError && (
                                    <div className="invalid-feedback">{passwordError}</div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="confirmPassword" className="form-label">{t('auth.resetPassword.confirmPassword')}</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    className={`form-control ${confirmError ? 'is-invalid' : ''}`}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        validateConfirmPassword(e.target.value);
                                    }}
                                    required
                                />
                                {confirmError && (
                                    <div className="invalid-feedback">{confirmError}</div>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-100"
                                disabled={loading}
                            >
                                {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetButton')}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;